import { createEffect, createMemo, createSignal, For, Show, untrack } from "solid-js";
import { createStore } from "solid-js/store";
import Modal from "../Modal";
import Text from "../Text";
import ItemStackView from "../ItemStackView";
import QuantityInput from "../QuantityInput";
import BotSelector from "../BotSelector";
import { useTranslation } from "../../hooks/useTranslation";
import { fromItemDef } from "../../services/itemRefAdapters";
import { formatQuantity } from "../../services/quantityFormatter";
import { useInventoryApi, type MovePlan } from "../../services/inventoryApi";
import type { CraftingResolverService } from "../../services/craftingResolver";
import type { ItemStack } from "../../models/itemStack";
import styles from "./MoveConfirmDialog.module.css";

export interface MoveConfirmItem {
  itemKey: string;
  available: number;
  stack?: ItemStack;
}

interface MoveConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  sourceStorageId: string;
  sourceStorageName: string;
  destinationStorageId: string;
  destinationStorageName: string;
  items: MoveConfirmItem[];
  resolver: CraftingResolverService | null;
  itemName: (key: string) => string;
  onConfirm: (rows: { itemKey: string; quantity: number }[], botId: string | null) => Promise<void>;
}

export default function MoveConfirmDialog(props: MoveConfirmDialogProps) {
  const { t } = useTranslation();
  const inv = useInventoryApi();

  const [quantities, setQuantities] = createStore<Record<string, number>>({});
  const [botId, setBotId] = createSignal<string | null>(null);
  const [eligibleBotIds, setEligibleBotIds] = createSignal<string[]>([]);
  const [plan, setPlan] = createSignal<MovePlan | null>(null);
  const [planLoading, setPlanLoading] = createSignal(false);
  const [submitting, setSubmitting] = createSignal(false);

  createEffect(() => {
    if (!props.open) return;
    const initial: Record<string, number> = {};
    for (const item of props.items) initial[item.itemKey] = item.available;
    setQuantities(initial);
    setBotId(null);
    setPlan(null);
  });

  createEffect(() => {
    const open = props.open;
    const source = props.sourceStorageId;
    const dest = props.destinationStorageId;
    if (!open || !source || !dest) { setEligibleBotIds([]); return; }
    untrack(() => { void loadEligibleBots(source, dest); });
  });

  const loadEligibleBots = async (source: string, dest: string) => {
    try {
      const sourceBots = await inv.fetchStorageBots(source);
      if (source === dest) {
        setEligibleBotIds(sourceBots.map(b => b.botRegistryId));
        return;
      }
      const destBots = await inv.fetchStorageBots(dest);
      const destIds = new Set(destBots.map(b => b.botRegistryId));
      setEligibleBotIds(sourceBots.map(b => b.botRegistryId).filter(id => destIds.has(id)));
    } catch {
      setEligibleBotIds([]);
    }
  };

  createEffect(() => {
    const open = props.open;
    const source = props.sourceStorageId;
    const dest = props.destinationStorageId;
    const bot = botId();
    if (!open || !source || !dest) { setPlan(null); return; }
    untrack(() => { void loadPlan(source, dest, bot); });
  });

  const loadPlan = async (source: string, dest: string, bot: string | null) => {
    setPlanLoading(true);
    try {
      setPlan(await inv.planMove(source, dest, bot));
    } catch {
      setPlan(null);
    } finally {
      setPlanLoading(false);
    }
  };

  const itemStack = (item: MoveConfirmItem, count: number): ItemStack => {
    if (item.stack) return { ...item.stack, count };
    const def = props.resolver?.getItem(item.itemKey);
    return def
      ? fromItemDef(def, count)
      : fromItemDef({ id: -1, metadata: 0, key: item.itemKey, displayName: item.itemKey, name: "air", stackSize: 64 }, count);
  };

  const stackSizeOf = (item: MoveConfirmItem) => props.resolver?.getItem(item.itemKey)?.stackSize;

  const rowPresets = (item: MoveConfirmItem) => {
    const stackSize = stackSizeOf(item) ?? 64;
    const presets: { label: string; value: number }[] = [];
    if (item.available >= stackSize) presets.push({ label: "1 Stack", value: stackSize });
    const half = Math.floor(item.available / 2);
    if (half > 0 && half !== stackSize) presets.push({ label: "Half", value: half });
    presets.push({ label: "Max", value: item.available });
    return presets;
  };

  const blockingError = createMemo(() => {
    if (planLoading()) return null;
    if (eligibleBotIds().length === 0) return t("INVENTORY_TRANSFER_NO_SHARED_BOT");
    const current = plan();
    if (current?.botError) return current.botError;
    return null;
  });

  const busyWarning = createMemo(() => {
    const current = plan();
    return current && !blockingError() && current.resolvedBotId && current.resolvedBotBusy
      ? t("INVENTORY_TRANSFER_BOT_BUSY_WARNING")
      : null;
  });

  const rowExceeds = (item: MoveConfirmItem) => {
    const qty = quantities[item.itemKey];
    return Number.isFinite(qty) && qty > item.available;
  };

  const rowsValid = createMemo(() =>
    props.items.every(item => {
      const qty = quantities[item.itemKey];
      return Number.isFinite(qty) && qty > 0 && qty <= item.available;
    })
  );

  const submitDisabled = () => submitting() || planLoading() || !rowsValid() || blockingError() !== null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const rows = props.items.map(item => ({ itemKey: item.itemKey, quantity: quantities[item.itemKey] }));
      await props.onConfirm(rows, botId());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      maxWidth="44rem"
      busy={submitting()}
      title={<Text val="INVENTORY_TRANSFER_TITLE" />}
      subtitle={
        <span class={styles.route}>
          <span class={styles.routeStorage}>{props.sourceStorageName}</span>
          <i class="fa-solid fa-arrow-right-long" />
          <span class={styles.routeStorage}>{props.destinationStorageName}</span>
        </span>
      }
    >
      <div class={styles.body}>
        <div class={styles.rows}>
          <For each={props.items}>
            {(item) => (
              <div class={styles.row}>
                <ItemStackView stack={itemStack(item, quantities[item.itemKey] ?? item.available)} size={36} />
                <div class={styles.rowInfo}>
                  <span class={styles.rowName}>{props.itemName(item.itemKey)}</span>
                  <span class={styles.rowAvailable}>{t("INVENTORY_TRANSFER_AVAILABLE", formatQuantity(item.available, stackSizeOf(item)))}</span>
                </div>
                <div class={styles.rowQuantity}>
                  <QuantityInput
                    value={quantities[item.itemKey] ?? item.available}
                    onChange={(v) => setQuantities(item.itemKey, v)}
                    min={1}
                    stackSize={stackSizeOf(item)}
                    presets={rowPresets(item)}
                    inline
                  />
                  <Show when={rowExceeds(item)}>
                    <span class={styles.rowError}>
                      <i class="fa-solid fa-triangle-exclamation" /> {t("INVENTORY_TRANSFER_NOT_ENOUGH", formatQuantity(item.available, stackSizeOf(item)))}
                    </span>
                  </Show>
                </div>
              </div>
            )}
          </For>
        </div>

        <div class="v" style={{ gap: "var(--spacing-xs)" }}>
          <span class={styles.fieldLabel}><Text val="INVENTORY_TRANSFER_BOT" /></span>
          <Show
            when={eligibleBotIds().length > 0}
            fallback={<p class={styles.blocking}><i class="fa-solid fa-robot" /> <Text val="INVENTORY_TRANSFER_NO_SHARED_BOT" /></p>}
          >
            <BotSelector
              storageKey="inventory_move_bot"
              allowedIds={eligibleBotIds()}
              forceBotId={plan()?.resolvedBotId ?? undefined}
              onSelect={(id) => setBotId(id)}
            />
          </Show>
        </div>

        <Show when={busyWarning()}>
          <p class={styles.warning}><i class="fa-solid fa-triangle-exclamation" /> {busyWarning()}</p>
        </Show>
        <Show when={blockingError() && eligibleBotIds().length > 0}>
          <p class={styles.blocking}><i class="fa-solid fa-triangle-exclamation" /> {blockingError()}</p>
        </Show>

        <div class={styles.actions}>
          <button type="button" class="btn btn-secondary" onClick={props.onClose}>
            <Text val="CANCEL" />
          </button>
          <button type="button" class="btn btn-primary" disabled={submitDisabled()} onClick={() => void submit()}>
            <Show when={submitting()} fallback={<><i class="fa-solid fa-right-left" /> <Text val="INVENTORY_TRANSFER_CONFIRM" /></>}>
              <i class="fa-solid fa-spinner fa-spin" />
            </Show>
          </button>
        </div>
      </div>
    </Modal>
  );
}
